import React, { useState } from 'react';
import { FileText, Download, ChevronRight } from 'lucide-react';
import { fmtBytes } from '../workflow/constants';

// Read-only view of an engagement's attachments, shared by the workflow detail
// pages (Engagement Amendment, Signed Financial Statements) so the "Engagement attachments" panel
// looks the same everywhere. Only the CURRENT version of each document shows;
// superseded / older versions collapse into "Previous versions". Labels are the
// document type (no legacy "CW/CC" prefix). See docs/modules/engagements.md §6.

const DOC_TYPE_LABELS = {
  QC6: 'QC6', QC7: 'QC7', QC7_PREV: 'Previous year QC',
  GROUP_STRUCTURE: 'Group structure', FS: 'Signed Financial Statements', QC35: 'QC35 Form', SUPPLEMENTARY: 'Supplementary',
  INDEPENDENCE_CONFIRMATION: 'Independence Confirmation',
  // The Registration's BizFile — it reaches this panel through
  // the engreg aggregation, already scoped to this engagement's own company.
  BIZFILE: 'BizFile',
  // The screening document — the requester's own (Registration answered No,
  // source engreg) or QM's, read from the screening record (source screening);
  // an engagement-owned upload supersedes either.
  SCREENING: 'Screening',
};

const badgeFor = (f) => {
  // Legacy wf_fs rows can carry a qc35 doc (pre-split) — label by docType.
  if (f.source === 'fs') return f.docType === 'qc35' ? 'QC35 Form' : 'Signed Financial Statements';
  if (f.source === 'qc35') return f.docType === 'screenshot' ? 'CaseWare screenshot' : 'QC35 Form';
  if (f.source === 'group') return 'Group structure';
  if (f.source === 'prev') return 'Previous year QC';
  // Generated on download from the engagement itself.
  if (f.source === 'indep') return 'Independence Confirmation';
  return DOC_TYPE_LABELS[f.docType] || f.docType || 'QC6/7';
};

/**
 * @param {Array} attachments - rows from GET .../attachments (carry superseded)
 * @param {(f) => string} [downloadHref] - builds the per-file download URL;
 *   omitted = list only, no download (Client Hub)
 */
const EngagementAttachments = ({ attachments = [], downloadHref }) => {
  const [showHistory, setShowHistory] = useState(false);
  if (attachments.length === 0) {
    return <p className="text-sm text-slate-500">No engagement documents.</p>;
  }
  const current = attachments.filter((f) => !f.superseded);
  const previous = attachments.filter((f) => f.superseded);

  const row = (f, muted) => (
    <li key={`${f.source}-${f.fileId}`} className={`flex items-center justify-between py-2 gap-2 ${muted ? 'opacity-60' : ''}`}>
      <span className="flex items-center gap-2 text-sm text-slate-700 min-w-0">
        <FileText className="w-4 h-4 shrink-0 text-slate-400" />
        <span className="truncate">{f.originalName}</span>
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 shrink-0">
          {badgeFor(f)}
        </span>
        {f.requestNo && <span className="text-xs text-slate-500 shrink-0">{f.requestNo}</span>}
        <span className="text-xs text-slate-500 shrink-0">{fmtBytes(f.sizeBytes)}</span>
      </span>
      {downloadHref && (
        <a href={downloadHref(f)} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 shrink-0">
          <Download className="w-4 h-4" /> Download
        </a>
      )}
    </li>
  );

  return (
    <>
      {current.length > 0 && <ul className="divide-y divide-slate-100">{current.map((f) => row(f, false))}</ul>}
      {previous.length > 0 && (
        <div className="border-t border-slate-100 pt-2 mt-1">
          <button type="button" onClick={() => setShowHistory((v) => !v)} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700">
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
            Previous Versions ({previous.length})
          </button>
          {showHistory && <ul className="divide-y divide-slate-100 mt-1">{previous.map((f) => row(f, true))}</ul>}
        </div>
      )}
    </>
  );
};

export default EngagementAttachments;
